import {
  type BaseQueryFn,
  createApi,
  type FetchArgs,
  fetchBaseQuery,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { logout } from "@/features/auth/slice";
import { clearFormDrafts } from "@/shared/utils/clearFormDrafts";
import { runtimeConfig } from "@/shared/config/runtime";

const rawBaseQuery = fetchBaseQuery({
  baseUrl: runtimeConfig.apiUrl,
  credentials: "include",
  prepareHeaders: (headers) => {
    headers.set("ngrok-skip-browser-warning", "true");
    return headers;
  },
});

function trimStrings(value: unknown): unknown {
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) return value.map(trimStrings);
  if (value !== null && typeof value === "object" && !(value instanceof FormData) && !(value instanceof Blob)) {
    const trimmed: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      trimmed[k] = trimStrings(v);
    }
    return trimmed;
  }
  return value;
}

let mockBaseQueryPromise: Promise<BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>> | null = null;

async function getMockBaseQuery(): Promise<BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>> {
  if (!mockBaseQueryPromise) {
    mockBaseQueryPromise = import("./mockBaseQuery").then(function pickMockBaseQuery(module) {
      return module.mockBaseQuery;
    });
  }
  return mockBaseQueryPromise;
}

let oneCClientPromise: Promise<typeof import("./oneC")> | null = null;

async function getOneCClient(): Promise<typeof import("./oneC")> {
  if (!oneCClientPromise) {
    oneCClientPromise = import("./oneC");
  }
  return oneCClientPromise;
}

let refreshPromise: Promise<{ data?: unknown; error?: FetchBaseQueryError }> | null = null;

const REFRESH_QUERY: FetchArgs = {
  url: "/api/auth/refresh/",
  method: "POST",
  credentials: "include",
};

function isHard401(result: { data?: unknown; error?: FetchBaseQueryError }): boolean {
  return result.error?.status === 401;
}

async function refreshWithRetry(
  api: Parameters<BaseQueryFn>[1],
  extraOptions: Parameters<BaseQueryFn>[2],
): Promise<{ data?: unknown; error?: FetchBaseQueryError }> {
  const first = await rawBaseQuery(REFRESH_QUERY, api, extraOptions);
  if ("data" in first && first.data) return first;
  if (isHard401(first)) return first;
  await new Promise((resolve) => setTimeout(resolve, 1000));
  return rawBaseQuery(REFRESH_QUERY, api, extraOptions);
}

export const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async function (
  args,
  api,
  extraOptions,
) {
  if (typeof args === "object" && args.body && !(args.body instanceof FormData) && !(args.body instanceof Blob)) {
    args = { ...args, body: trimStrings(args.body) };
  }

  if (runtimeConfig.useMockApi) {
    const mockBaseQuery = await getMockBaseQuery();
    return mockBaseQuery(args, api, extraOptions);
  }

  if (runtimeConfig.oneCAccounting.enabled) {
    const { resolveOneCRequest, oneCBaseQuery, resolveAccountingMock } = await getOneCClient();
    const decision = resolveOneCRequest(args, runtimeConfig.oneCAccounting.source === "mock");
    if (decision.kind === "block") return { error: decision.error };
    if (decision.kind === "forward") {
      if (runtimeConfig.oneCAccounting.source === "mock") {
        return resolveAccountingMock(args);
      }
      const forwarded = typeof args === "string" ? decision.url : { ...args, url: decision.url };
      return oneCBaseQuery(forwarded, api, extraOptions);
    }
  }

  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const url = typeof args === "string" ? args : (args as FetchArgs).url;

    if (url?.includes("/api/auth/login/")) {
      return result;
    }

    // Guest signing runs without an E-NGO session: its 401 means the guest
    // cookie is missing or corrupted, never that the user's session expired.
    if (url?.includes("/api/signing/public/")) {
      return result;
    }

    // Same for the V2 invitation page: the secret lives in the link, not in a
    // session, so a bad or expired one must not log the visitor out.
    if (url?.includes("/api/signing/v2/invitations/")) {
      return result;
    }

    // The public SROI respondent survey (`/survey/:token`) is AllowAny with
    // no auth at all — a stale session cookie from an unrelated tab must
    // never bounce an anonymous respondent into a login redirect.
    if (url?.includes("/api/sroi/public/")) {
      return result;
    }

    const errorData = result.error.data as { detail?: string } | undefined;
    if (errorData?.detail === "This token can only be used to change password") {
      return result;
    }

    if (!refreshPromise) {
      refreshPromise = (async () => {
        try {
          return await refreshWithRetry(api, extraOptions);
        } finally {
          refreshPromise = null;
        }
      })();
    }

    const refreshResult = await refreshPromise;

    if (refreshResult && "data" in refreshResult && refreshResult.data) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      result = await rawBaseQuery(args, api, extraOptions);
    } else {
      // The session probe is the first request of every page load. Its 401 after a
      // failed refresh means "guest", which the router handles itself; an expired
      // access token with a live refresh token never gets here.
      if (url?.includes("/api/auth/me/")) {
        return result;
      }
      clearFormDrafts();
      api.dispatch(logout());
      api.dispatch(baseApi.util.resetApiState());
    }
  }

  if (result.error && result.error.status === 429) {
    const url = typeof args === "string" ? args : (args as FetchArgs).url;

    if (url?.includes("/api/auth/login/")) {
      return result;
    }
  }

  return result;
};

export const API_TAG_TYPES = [
  "Auth",
  "Notifications",
  "Applications",
  "Completeness",
  "Contracts",
  "NGOWorkers",
  "VehicleRentals",
  "PremisesLeases",
  "LegalDocuments",
  "ServiceAgreementsIndividual",
  "VehicleHandovers",
  "PremisesHandovers",
  "ServiceAgreementsMSB",
  "GoodsSupply",
  "PlatformAccess",
  "CustomDocuments",
  "TargetedFinancing",
  "GuestSigning",
  "LeaveApplications",
  "ResignationLetters",
  "Amendments",
  "Timesheets",
  "Payrolls",
  "Purchases",
  "InventoryItems",
  "InventoryUnits",
  "Holidays",
  "Reports",
  "Budget",
  "PoolBalances",
  "RentalPayments",
  "SelfService",
  "GphContract",
  "PendingActions",
  "ItemRequests",
  "Projects",
  "ProjectEvents",
  "ProjectPublications",
  "ProjectReports",
  "AnnualReports",
  "EsgReports",
  "Tasks",
  "TaskComments",
  "AuditEntries",
  "ProjectMembers",
  "TimesheetAllocations",
  "CompletionActs",
  "SigningDocuments",
  "SigningOperations",
  "SigningDocumentsV2",
  "GuestInvitations",
  "BankAccounts",
  "TaskBoard",
  "TaskGoals",
  "IncomeRecords",
  "AttachedDocuments",
  "AttachedDocumentFolders",
  "Organization",
  "Registry",
  "Publicity",
  "GPHPayments",
  "TeamLeaveSchedule",
  "SmetaData",
  "FSMSubmissions",
  "Volunteers",
  "Experts",
  "WorkerDetail",
  "ChatChannels",
  "ChatPeople",
  "DataChangeRequests",
  "WorkerIdDocumentHistory",
  "MsbContract",
  "ImportWorkerDrafts",
  "ContractFillDrafts",
  "PersonalSheetDraft",
  "PersonalDataChangeDraft",
  "Advances",
  "BusinessTrips",
  "Departments",
  "Managers",
  "StaffingTables",
  "Sroi",
  "SupportTicket",
] as const;

export const baseApi = createApi({
  reducerPath: "baseApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [...API_TAG_TYPES],
  endpoints: () => ({}),
});

